// The project modal's floating Cancel / Save Changes bar. Extracted verbatim from
// ProjectModalTabRenderer (FIN-3.5) so the finance layout editor can reuse the exact
// same control instead of shipping a second, differently-styled save button.
//
// Renders nothing when there is nothing to save — that null return is what the
// Playwright POM waits on (`finance-save-layout` detaching) to confirm a save landed.

import { useCanWriteProject } from '@/hooks/usePartnerScoped';

interface FloatingSaveBarProps {
  hasUnsavedChanges: boolean;
  saving: boolean;
  onCancel: () => void;
  onSave: () => void;
  /**
   * Stacking context for the bar. Defaults to the project modal's layer; the finance
   * section raises it when it renders standalone inside a drawer (drawers start at 220).
   */
  zIndexClass?: string;
  /** Optional test id for the save button (e.g. 'finance-save-layout'). */
  saveTestId?: string;
}

export function FloatingSaveBar({
  hasUnsavedChanges,
  saving,
  onCancel,
  onSave,
  zIndexClass = 'z-[190]',
  saveTestId,
}: FloatingSaveBarProps) {
  // RBAC-3.93: a partner-scoped caller saves nothing on a project, so is never offered Save.
  const canWrite = useCanWriteProject();
  if (!hasUnsavedChanges || !canWrite) return null;

  return (
    <div className={`fixed bottom-6 right-6 flex gap-2 ${zIndexClass}`}>
      <button
        type="button"
        onClick={onCancel}
        className="px-4 py-2 text-sm font-medium bg-background text-foreground hover:bg-muted rounded-md transition-all shadow-lg border border-border"
        disabled={saving}
      >
        Cancel
      </button>
      <button
        type="button"
        data-testid={saveTestId}
        onClick={onSave}
        disabled={saving || !hasUnsavedChanges}
        className="px-5 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
      >
        {saving ? (
          <span className="flex items-center">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Saving...
          </span>
        ) : (
          "Save Changes"
        )}
      </button>
    </div>
  );
}
